/** Public SF311 ticket with status, description, photos, and activity. */
export function sf311CaseSourceUrl(id: string) {
  return `https://mobile311.sfgov.org/tickets/${encodeURIComponent(id)}`;
}

/** The parcel report includes the Assessor Summary and Secured Property Tax Rolls. */
export function assessorSourceUrl(parcelNumber: string) {
  const url = new URL('https://sfplanninggis.org/pim/');
  url.searchParams.set('search', parcelNumber);
  return url.toString();
}

/** Treasurer & Tax Collector bill lookup: annual, supplemental and escape bills with payment status. */
export function treasurerSourceUrl(parcelNumber: string) {
  // The legacy host can redirect a challenge POST into a 405 response here.
  const url = new URL('https://county-taxes.net/ca-sanfrancisco/property-tax');
  url.searchParams.set('search_query', parcelNumber);
  return url.toString();
}

/**
 * Navigation policy for city sources, shared by web views and alert emails.
 * Stored provenance may identify an API or dataset; it is not a record link.
 * Keep this module free of scrapers so it is safe in client and email bundles.
 */
export function humanSourceUrl(
  value: string | undefined,
  record?: { kind?: string; recordId?: string },
) {
  // Resolve legacy snapshots and queued alerts from their known record identity.
  if (record?.kind === '311' && /^\d{1,20}$/.test(record.recordId ?? ''))
    return sf311CaseSourceUrl(record.recordId!);
  if (record?.kind === 'subdivision' && /^\d{1,20}$/.test(record.recordId ?? ''))
    return `https://bsm.sfdpw.org/subdivision/tracking/subdProject.asp?pid=${record.recordId}`;
  if (record?.kind === 'assessor') {
    const parcel = record.recordId?.match(/^(\d{4}[A-Z]?\/?\d{3}[A-Z]?)(?: · \d{4})?$/i)?.[1];
    if (parcel) return assessorSourceUrl(parcel.replace('/', ''));
  }
  if (!value || value.length > 1024) return undefined;
  try {
    const url = new URL(value);
    if (
      !['https:', 'http:'].includes(url.protocol) ||
      url.username ||
      url.password ||
      url.href.length > 1024
    )
      return undefined;

    const host = url.hostname.toLowerCase().replace(/\.$/, '');
    const path = url.pathname.toLowerCase().replace(/\/+$/, '');
    if (['data.sf.gov', 'data.sfgov.org', 'datasf.org'].includes(host)) return undefined;
    if (/\/(?:api|resource|rest\/services)(?:\/|$)/.test(path) || /\.(?:json|csv|xml)$/.test(path))
      return undefined;

    // Search homepages and citywide maps don't identify the displayed record.
    if (host === 'sfplanninggis.org' && path === '/pim' && !url.searchParams.get('search')?.trim())
      return undefined;
    if (host === 'recorder.sfgov.org' && path === '') return undefined;
    if (
      host === 'sanfrancisco.agencycounter.com' &&
      (path === '' || path === '/search' || path.startsWith('/search/saved/'))
    )
      return undefined;
    if (
      host === 'bsm.sfdpw.org' &&
      ['/subdivision', '/subdivision/tracking', '/subdivision/tracking/subdbottom.asp'].includes(
        path,
      )
    )
      return undefined;
    if (
      host === 'dbiweb02.sfgov.org' &&
      /^\/dbipts(?:\/default2?\.aspx)?$/.test(path) &&
      ![...url.searchParams].some(
        ([key, value]) =>
          /^(?:block|addressid|permitnumber|permitno|complaintno|comp_id)$/i.test(key) &&
          value.trim(),
      )
    )
      return undefined;
    return url.href;
  } catch {
    return undefined;
  }
}
